/* eslint-disable @typescript-eslint/camelcase */

import React, { FC, useEffect } from 'react'
import { IntlProvider, FormattedNumber } from 'react-intl'
import { ThemeProvider } from 'styled-components'

import { MultiSelect, Loader, Slider, InputCurrency, TooltipNew, theme } from '@elamajs/ui-kit'
import { Link } from '@iui-kit/components/new'

import { getSvg } from '@src/containers/UtmGenerator/helpers'
import { messages } from '@src/containers/CommissionCalculator/messages'

import { SYSTEM_CODE, NO_REWARD_SYSTEM_LIST } from '../../constants'
import {
  useCommissionTiers,
  useAvailableSystems,
  useFixScroll,
  useMinTotalTurnover,
  useDefaultSystems
} from '../../hooks'
import { getSystemLogo, getPartnerReward, getNewPartnerReward } from '../../helpers'

import * as Styled from './PartnersCalculator.styled'

type OwnProps = {
  isNewTab?: boolean
}

const PartnersCalculator: FC<OwnProps> = ({ isNewTab = true }) => {
  /* Фикс нежелательного скролла при фокусе на инпуте в Android Chrome */
  const { fixScrollInAndroidChrome } = useFixScroll()
  useEffect(fixScrollInAndroidChrome, [])

  /* Получение списка доступных систем */
  const { availableSystems, loadAvailableSystems } = useAvailableSystems(isNewTab)
  useEffect(loadAvailableSystems, [isNewTab])

  /* Получение минимального оборота */
  const { minTotalTurnover, loadMinTotalTurnover } = useMinTotalTurnover()
  useEffect(loadMinTotalTurnover, [])

  /* Установка систем по умолчанию */
  const { turnovers, setTurnovers, selectSystemsByDefault } = useDefaultSystems({
    isNewTab,
    availableSystems,
    minTotalTurnover
  })
  useEffect(selectSystemsByDefault, [availableSystems, minTotalTurnover, isNewTab])

  /* тиры вознаграждений(тот процент, по которому будет считаться вознаграждение в зависимости от размера оборота) */

  const { getTierInfo } = useCommissionTiers(turnovers, isNewTab)
  /* Расчет вознаграждения */
  const partnerReward = isNewTab ? getNewPartnerReward(turnovers) : getPartnerReward(turnovers)

  /* рендеринг калькулятора */
  // @TODO: Копипаст из текущего компонента калькулятора, после A/B вынести в отдельный хук
  const renderCalculator = () => {
    if (!availableSystems || !minTotalTurnover || !turnovers) {
      return (
        <Styled.LoaderContainer>
          <Loader />
        </Styled.LoaderContainer>
      )
    }

    const availableSystemCodes = Object.keys(availableSystems)

    const sortedSelectedSystemCodes = availableSystemCodes.filter(code => {
      return code in turnovers
    })

    const renderSystemSelect = () => {
      const options = availableSystemCodes.map(code => {
        return {
          label: availableSystems[code],
          caption: '',
          value: code
        }
      })

      const handleChange = (codes: string[]) => {
        const values = Object.fromEntries(
          codes.map(code => {
            const { [code]: turnover = minTotalTurnover } = turnovers

            return [code, turnover]
          })
        )
        setTurnovers(values)
      }

      return (
        <Styled.MultiSelect>
          <MultiSelect
            options={options}
            optionsLabel="Системы"
            value={sortedSelectedSystemCodes}
            onChange={handleChange}
            width="100%"
            hasAllCheckbox
            hasSearch
          />
        </Styled.MultiSelect>
      )
    }

    const renderTurnoverInputs = () => {
      const minSliderValue = 0

      const maxValue = 999999999

      return (
        <Styled.SystemsContainer>
          {sortedSelectedSystemCodes.map(systemCode => {
            const maxSliderValue = 10000000
            const systemName = availableSystems[systemCode]

            const value = Math.min(turnovers[systemCode], maxValue)
            const sliderValue = Math.max(minSliderValue, Math.min(value, maxSliderValue))

            const handleChange = (value: number) => {
              const currentValue = isNaN(value) ? 0 : Math.min(value, maxValue)

              setTurnovers(previousTurnovers => {
                const newTurnovers = {
                  ...previousTurnovers,
                  [systemCode]: currentValue
                }
                return newTurnovers
              })
            }
            const renderAdditionalText = () => {
              const { rates, activeIndex } = getTierInfo(systemCode)

              const renderPercentages = () => {
                if (isNewTab) {
                  return (
                    <Styled.PercentageContainer>
                      <Styled.PercentageValue isActive>{rates[0]}%</Styled.PercentageValue>
                    </Styled.PercentageContainer>
                  )
                }

                return (
                  <Styled.PercentageContainer>
                    {rates.map((rate, index) => (
                      <Styled.PercentageValue key={index} isActive={activeIndex === index}>
                        {rate}%
                      </Styled.PercentageValue>
                    ))}
                  </Styled.PercentageContainer>
                )
              }

              if (systemCode === SYSTEM_CODE.YANDEX_NETWORK) {
                return (
                  <Styled.SystemSliderAdditionalText>
                    {renderPercentages()}
                  </Styled.SystemSliderAdditionalText>
                )
              }

              if (NO_REWARD_SYSTEM_LIST.includes(systemCode)) {
                const tooltip = (
                  <Styled.TooltipContent>
                    Увеличивает ваш общий оборот. Оборот по&nbsp;данной системе повышает ваш процент
                    по&nbsp;остальным системам
                  </Styled.TooltipContent>
                )
                return (
                  <Styled.SystemSliderAdditionalTooltip>
                    Вознаграждение за рекламу не&nbsp;выплачиваем.
                    <Styled.Tooltip>
                      <TooltipNew
                        content={tooltip}
                        icon="questionNew"
                        direction="top-start"
                        width="295px"
                      />
                    </Styled.Tooltip>
                  </Styled.SystemSliderAdditionalTooltip>
                )
              }

              return (
                <Styled.SystemSliderAdditionalText>
                  {renderPercentages()}
                </Styled.SystemSliderAdditionalText>
              )
            }

            return (
              <Styled.SystemSliderContainer key={systemCode}>
                <Styled.SystemSliderInfoWrapper>
                  <Styled.SystemSliderTitle>
                    {getSystemLogo(systemCode, systemName)}
                    {systemName}
                  </Styled.SystemSliderTitle>
                  <Styled.SystemSliderTextContainer>
                    {renderAdditionalText()}
                  </Styled.SystemSliderTextContainer>
                </Styled.SystemSliderInfoWrapper>
                <Styled.SystemSlider>
                  <Styled.Slider
                    as={Slider}
                    min={minSliderValue}
                    max={maxSliderValue}
                    value={Math.round(sliderValue)}
                    measure="₽"
                    width={'100%' as const}
                    onChange={handleChange}
                  />
                  <Styled.SystemTurnoverWrapper>
                    <InputCurrency
                      value={String(value)}
                      currency="RUB"
                      width="100%"
                      isFractional={false}
                      textAlign="left"
                      onChange={value => {
                        handleChange(Number(value))
                      }}
                    />
                  </Styled.SystemTurnoverWrapper>
                </Styled.SystemSlider>
              </Styled.SystemSliderContainer>
            )
          })}
        </Styled.SystemsContainer>
      )
    }

    return (
      <>
        {renderSystemSelect()}
        {renderTurnoverInputs()}
      </>
    )
  }

  /* расчёт значений для блока Info */

  const currency = 'RUB'

  const selectedSystemCodes = turnovers ? Object.keys(turnovers) : []

  const onlyNoRewardSystemsSelected =
    selectedSystemCodes.length > 0 &&
    selectedSystemCodes.every(systemCode => {
      return NO_REWARD_SYSTEM_LIST.includes(systemCode)
    })

  const totalTurnover = turnovers
    ? Object.values(turnovers).reduce((sum, turnover) => sum + turnover, 0)
    : 0

  const totalTurnoverIsLow = !!minTotalTurnover && totalTurnover < minTotalTurnover

  // @TODO: Копипаст из текущего компонента калькулятора, после A/B вынести в отдельный хук
  const renderRewardValue = () => {
    if (onlyNoRewardSystemsSelected) {
      return (
        <FormattedNumber value={0} currency={currency} style="currency" maximumFractionDigits={0} />
      )
    }

    if (totalTurnoverIsLow) {
      return (
        <FormattedNumber value={0} currency={currency} style="currency" maximumFractionDigits={0} />
      )
    }

    if (partnerReward === null) {
      return <Loader />
    }

    return (
      <FormattedNumber
        value={partnerReward}
        currency={currency}
        style="currency"
        maximumFractionDigits={0}
      />
    )
  }
  // @TODO: Копипаст из текущего компонента калькулятора, после A/B вынести в отдельный хук
  const renderError = () => {
    if (onlyNoRewardSystemsSelected && availableSystems) {
      return (
        <Styled.ResultError>
          По выбранным рекламным системам вознаграждение не выплачивается
        </Styled.ResultError>
      )
    }

    if (totalTurnoverIsLow && minTotalTurnover) {
      return (
        <Styled.ResultError>
          Вознаграждение рассчитывается при суммарном обороте более{' '}
          <FormattedNumber value={minTotalTurnover} maximumFractionDigits={0} /> рублей
        </Styled.ResultError>
      )
    }

    return null
  }

  const PROGRESS_BAR_MAX = 3_000_000
  const progressPercent = (totalTurnover / PROGRESS_BAR_MAX) * 100

  return (
    <Styled.ContentBlock>
      <Styled.MobileTitle>Калькулятор вознаграждения</Styled.MobileTitle>
      <Styled.Info>
        <h3>Калькулятор вознаграждения</h3>
        <Styled.Calculations>
          <Styled.CalculationContainer>
            <Styled.Calculation>
              <Styled.Label>Планируемый оборот за&nbsp;месяц</Styled.Label>
              <Styled.Value>
                <FormattedNumber
                  value={totalTurnover}
                  currency={currency}
                  style="currency"
                  maximumFractionDigits={0}
                />
              </Styled.Value>
            </Styled.Calculation>
            {!isNewTab && (
              <Styled.ProgressBarTrack>
                <Styled.ProgressBarLabel>
                  <p>30&nbsp;000&nbsp;&mdash; 499&nbsp;999&nbsp;₽</p>
                  <p>500&nbsp;000&nbsp;&mdash; 2&nbsp;999&nbsp;999&nbsp;₽</p>
                  <p>от&nbsp;3&nbsp;000&nbsp;000&nbsp;₽</p>
                </Styled.ProgressBarLabel>
                <Styled.ProgressBarFill percent={progressPercent} />
              </Styled.ProgressBarTrack>
            )}
          </Styled.CalculationContainer>
          <Styled.Calculation>
            <Styled.Label>Ваше вознаграждение</Styled.Label>
            <Styled.Value>{renderRewardValue()}</Styled.Value>
          </Styled.Calculation>
        </Styled.Calculations>
        {renderError()}
        <Styled.TextBlock>
          <Styled.Text>
            {isNewTab ? (
              <>Калькулятор рассчитывает размер вознаграждения на&nbsp;период акции</>
            ) : (
              <>Калькулятор рассчитывает только базовое вознаграждение без дополнительного</>
            )}
          </Styled.Text>
          <Styled.Text>
            Расчет предварительный, уточняйте детали в&nbsp;условиях Партнерской программы
          </Styled.Text>
          <Styled.Text>Не&nbsp;является публичной офертой.</Styled.Text>
        </Styled.TextBlock>
        <Styled.LinkBlock>
          <Link
            target="_blank"
            href="https://elama.ru/legal_docs/usloviya-partnerskoy-programmy/partners-program_smb.pdf"
            rel="noreferrer"
          >
            Полные условия
          </Link>
          {getSvg('arrow')}
        </Styled.LinkBlock>
      </Styled.Info>
      <Styled.Calculator>{renderCalculator()}</Styled.Calculator>
    </Styled.ContentBlock>
  )
}

const withProviders = (Component: FC<OwnProps>): FC<OwnProps> => {
  const NewComponent: FC<OwnProps> = props => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru" messages={messages}>
          <Component {...props} />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(PartnersCalculator)
